/* oxlint-disable */
'use client';

import type React from 'react';

import {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';

import {
  motion, type PanInfo, useMotionValue, animate, useSpring,
} from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';

// Hook personalizado para detectar mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => { return window.removeEventListener('resize', checkIsMobile); };
  }, []);

  return isMobile;
};

export interface HCarouselProps {
  items: React.ReactNode[]
  gap?: number
  blur?: boolean
  animated?: boolean
  autoPlay?: boolean
  showDots?: boolean // Nueva prop para mostrar/ocultar dots
  dotsClassName?: string // Clases CSS personalizadas para los dots
  opts?: {
    align?: 'start' | 'center' | 'end'
    mobileAlign?: 'start' | 'center' | 'end'
    startIndex?: number
    duration?: number
    active?: boolean
  }
}

const HCarousel = ({
  items,
  gap = 16,
  blur = false,
  animated = false,
  autoPlay = false,
  showDots = false,
  dotsClassName = '',
  opts = {
    align: 'start',
    mobileAlign: 'start',
    startIndex: 0,
    duration: 0.3,
    active: true,
  },
}: HCarouselProps) => {
  const isMobile = useIsMobile();
  const [currentIndex, setCurrentIndex] = useState(opts.startIndex || 0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [itemWidth, setItemWidth] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(1);
  const [isReady, setIsReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const x = useMotionValue(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Usar useSpring para un movimiento más suave con rebote
  const springX = useSpring(x, {
    stiffness: 300,
    damping: 25,
    mass: 0.8,
  });

  // Función para medir automáticamente los items
  const measureItems = useCallback(() => {
    if (!containerRef.current || items.length === 0) return;

    const container = containerRef.current;
    const containerW = container.offsetWidth;

    // Obtener el primer item para medir su ancho natural
    const firstItem = itemsRef.current[0];
    if (!firstItem) return;

    const itemW = firstItem.offsetWidth;

    const itemsWithGap = itemW + gap;
    const maxItemsPerView = Math.floor((containerW + gap) / itemsWithGap);
    const finalItemsPerView = Math.max(1, Math.min(maxItemsPerView, items.length));

    setContainerWidth(containerW);
    setItemWidth(itemW);
    setItemsPerView(finalItemsPerView);
    setIsReady(true);
  }, [items.length, gap]);

  const extendedItems = useMemo(() => {
    if (!isAutoPlaying || items.length === 0) return items;

    // Duplicar algunos items al final y al inicio para transición suave
    const itemsToClone = Math.min(3, items.length);
    const startClones = items.slice(-itemsToClone);
    const endClones = items.slice(0, itemsToClone);

    return [...startClones, ...items, ...endClones];
  }, [items, isAutoPlaying]);

  const { maxIndex } = useMemo(() => {
    if (!isReady) return { maxIndex: 0, realStartIndex: 0 };

    if (isAutoPlaying) {
      const itemsToClone = Math.min(3, items.length);
      return {
        maxIndex: extendedItems.length - 1,
        realStartIndex: itemsToClone,
      };
    }

    return {
      maxIndex: Math.max(0, items.length - itemsPerView),
      realStartIndex: 0,
    };
  }, [isReady, items.length, itemsPerView, isAutoPlaying, extendedItems.length]);

  const getXPosition = useCallback(
    (index: number) => {
      if (!isReady) return 0;

      const position = -index * (itemWidth + gap);

      const currentAlign = isMobile ? opts.mobileAlign : 'center';
      if (currentAlign === 'center' && !isMobile && !isAutoPlaying) {
        const visibleWidth = itemWidth * itemsPerView + gap * (itemsPerView - 1);
        const centerOffset = (containerWidth - visibleWidth) / 2;
        return position + centerOffset;
      }

      return position;
    },
    [isReady, itemWidth, gap, isMobile, opts.mobileAlign, containerWidth, itemsPerView, isAutoPlaying],
  );

  const handleInfiniteLoop = useCallback(
    (newIndex: number) => {
      if (!isAutoPlaying) return newIndex;

      const itemsToClone = Math.min(3, items.length);
      const realItemsStart = itemsToClone;
      const realItemsEnd = itemsToClone + items.length;

      if (newIndex >= realItemsEnd) {
        setIsTransitioning(true);
        const targetX = getXPosition(realItemsStart);

        animate(x, targetX, {
          type: 'spring',
          stiffness: 250,
          damping: 20,
          mass: 1,
          duration: 0.8,
        }).then(() => {
          setCurrentIndex(realItemsStart);
          setIsTransitioning(false);
        });

        return newIndex;
      }

      if (newIndex < realItemsStart) {
        setIsTransitioning(true);
        const targetIndex = realItemsEnd - 1;
        const targetX = getXPosition(targetIndex);

        animate(x, targetX, {
          type: 'spring',
          stiffness: 250,
          damping: 20,
          mass: 1,
          duration: 0.8,
        }).then(() => {
          setCurrentIndex(targetIndex);
          setIsTransitioning(false);
        });

        return newIndex;
      }

      return newIndex;
    },
    [isAutoPlaying, items.length, getXPosition, x],
  );

  const handleDragEnd = useCallback(
    (event: any, info: PanInfo) => {
      if (!opts.active || !animated || isTransitioning) return;

      if (isAutoPlaying) {
        setIsAutoPlaying(false);
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
          autoPlayRef.current = null;
        }
      }

      const threshold = 50;
      const velocity = info.velocity.x;
      const offset = info.offset.x;

      let newIndex = currentIndex;

      if (Math.abs(offset) > threshold || Math.abs(velocity) > 500) {
        if (offset > 0 || velocity > 500) {
          newIndex = Math.max(0, currentIndex - 1);
        } else {
          newIndex = Math.min(maxIndex, currentIndex + 1);
        }
      }

      if (newIndex === currentIndex && Math.abs(offset) > threshold) {
        const bounceDistance = 30;
        const bounceDirection = offset > 0 ? bounceDistance : -bounceDistance;
        const currentX = getXPosition(currentIndex);

        animate(x, currentX + bounceDirection, {
          type: 'spring',
          stiffness: 400,
          damping: 15,
          mass: 0.5,
          duration: 0.3,
        }).then(() => {
          animate(x, currentX, {
            type: 'spring',
            stiffness: 300,
            damping: 25,
            mass: 0.8,
            duration: 0.4,
          });
        });
        return;
      }

      const finalIndex = handleInfiniteLoop(newIndex);
      if (finalIndex === newIndex) {
        setCurrentIndex(newIndex);
      }
    },
    [
      currentIndex,
      maxIndex,
      opts.active,
      animated,
      isAutoPlaying,
      isTransitioning,
      handleInfiniteLoop,
      getXPosition,
      x,
    ],
  );

  useEffect(() => {
    if (!animated || !isReady || isTransitioning) return;

    const targetX = getXPosition(currentIndex);

    animate(x, targetX, {
      type: 'spring',
      stiffness: 280,
      damping: 22,
      mass: 0.9,
      duration: opts.duration || 0.6,
    });
  }, [currentIndex, getXPosition, x, animated, isReady, isTransitioning, opts.duration]);

  useEffect(() => {
    const measureContainer = () => {
      measureItems();
    };

    const timer = setTimeout(measureContainer, 100);

    window.addEventListener('resize', measureContainer);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', measureContainer);
    };
  }, [measureItems]);

  useEffect(() => {
    if (isAutoPlaying && isReady && !isTransitioning) {
      const itemsToClone = Math.min(3, items.length);
      const initialIndex = itemsToClone + (opts.startIndex || 0);
      setCurrentIndex(initialIndex);
    }
  }, [isAutoPlaying, isReady, items.length, opts.startIndex, isTransitioning]);

  const handleAutoPlayNext = useCallback(() => {
    setCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + 1;
      handleInfiniteLoop(nextIndex);
      return nextIndex;
    });
  }, [handleInfiniteLoop]);

  useEffect(() => {
    if (!isAutoPlaying || !animated || !isReady || isTransitioning) return;

    const startAutoPlay = () => {
      autoPlayRef.current = setInterval(handleAutoPlayNext, 3500);
    };

    startAutoPlay();

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
        autoPlayRef.current = null;
      }
    };
  }, [isAutoPlaying, animated, isReady, isTransitioning, handleAutoPlayNext]);

  if (!isReady) {
    return (
      <div
        ref={containerRef}
        className="overflow-x-hidden overflow-y-visible w-full opacity-0"
        style={{
          maskImage: blur ? 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)' : 'none',
        }}
      >
        <div className="flex" style={{ gap: `${gap}px` }}>
          {items.map((item, index) => {
            return (
              <div
                key={uuidv4()}
                ref={(el) => {
                  itemsRef.current[index] = el;
                }}
                className="flex-shrink-0"
              >
                {item}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (animated) {
    const itemsToRender = isAutoPlaying ? extendedItems : items;

    return (
      <div
        ref={containerRef}
        className="overflow-x-hidden overflow-y-visible w-full"
        style={{
          maskImage: blur ? 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)' : 'none',
        }}
      >
        <motion.div
          className="flex"
          style={{
            x: springX,
            gap: `${gap}px`,
          }}
          drag={opts.active && !isTransitioning ? 'x' : false}
          dragConstraints={{
            left: -((itemsToRender.length - 1) * (itemWidth + gap)),
            right: 0,
          }}
          dragElastic={0.15}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
          transition={{
            type: 'spring',
            stiffness: 280,
            damping: 22,
            mass: 0.9,
          }}
        >
          {itemsToRender.map((item, index) => {
            return (
              <motion.div
                key={`carousel-item-${isAutoPlaying ? 'extended' : 'normal'}-${index}`}
                className="flex-shrink-0"
                style={{ width: `${itemWidth}px` }}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 20,
                  mass: 0.8,
                  delay: index * 0.05,
                }}
                whileHover={{
                  scale: 1.02,
                  y: -2,
                  transition: {
                    type: 'spring',
                    stiffness: 400,
                    damping: 15,
                    mass: 0.5,
                  },
                }}
              >
                {item}
              </motion.div>
            );
          })}
        </motion.div>

        {/* Dots de navegación opcionales */}
        {showDots && items.length > 1 && (
          <div className={`flex justify-center gap-2 mt-4 ${dotsClassName}`}>
            {items.map((_, index) => {
              // Para autoplay, necesitamos mapear el índice real
              let realIndex = index;
              if (isAutoPlaying) {
                const itemsToClone = Math.min(3, items.length);
                realIndex = index - itemsToClone;
                if (realIndex < 0 || realIndex >= items.length) return null;
              }

              const isActive = isAutoPlaying ? currentIndex === index : currentIndex === realIndex;

              return (
                <button
                  type="button"
                  key={`dot-${index}`}
                  className="w-2 h-2 rounded-full transition-all duration-200 cursor-pointer"
                  style={{
                    backgroundColor: isActive ? '#28CDDF' : '#71717a',
                    borderColor: isActive ? '#28CDDF' : '#71717a',
                  }}
                  onClick={() => {
                    if (isAutoPlaying) {
                      const targetIndex = index;
                      setCurrentIndex(targetIndex);
                    } else {
                      setCurrentIndex(realIndex);
                    }
                  }}
                  aria-label={`Go to slide ${realIndex + 1}`}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="overflow-x-hidden overflow-y-visible w-full"
      style={{
        maskImage: blur ? 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)' : 'none',
      }}
    >
      <div className="flex" style={{ gap: `${gap}px` }}>
        {items.map((item, index) => {
          return (
            <motion.div
              key={`carousel-item-static-${index}`}
              className="flex-shrink-0"
              style={{ width: `${itemWidth}px` }}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                type: 'spring',
                stiffness: 250,
                damping: 18,
                mass: 1,
                delay: index * 0.1,
              }}
              whileHover={{
                scale: 1.05,
                y: -5,
                transition: {
                  type: 'spring',
                  stiffness: 400,
                  damping: 12,
                  mass: 0.6,
                },
              }}
            >
              {item}
            </motion.div>
          );
        })}
      </div>

      {/* Dots de navegación opcionales para versión no animada */}
      {showDots && items.length > 1 && (
        <div className={`flex justify-center gap-2 mt-4 ${dotsClassName}`}>
          {items.map((_, index) => {
            return (
              <button
                type="button"
                key={`dot-static-${index}`}
                className="w-2 h-2 rounded-full transition-all duration-200 cursor-pointer"
                style={{
                  backgroundColor: currentIndex === index ? '#28CDDF' : '#71717a',
                  borderColor: currentIndex === index ? '#28CDDF' : '#71717a',
                }}
                onClick={() => { return setCurrentIndex(index); }}
                aria-label={`Go to slide ${index + 1}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HCarousel;
